import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Typography } from '@mui/material';
import { BookingOrigin } from '@utriper/core';
import { Alert } from '@utriper/core-ui';

export const OTA_ALERT_STORAGE_KEY = 'hideOtaPriceActivationAlert';

// Same rule the API applies (BookingOrigin.isOTABooking), so the alert and the backend agree on
// what an OTA is. Form values arrive as numbers or numeric strings depending on the control.
export const hasOtaOrigin = (bookingOrigins = []) => (Array.isArray(bookingOrigins) ? bookingOrigins : []).some((id) => BookingOrigin.isOTABooking(Number(id)));

// localStorage can throw (private mode, blocked storage); the alert then simply shows every time.
const readHidden = () => {
    try {
        return window.localStorage.getItem(OTA_ALERT_STORAGE_KEY) === 'true';
    } catch {
        return false;
    }
};

const writeHidden = () => {
    try {
        window.localStorage.setItem(OTA_ALERT_STORAGE_KEY, 'true');
    } catch {
        // nothing to do: the choice lasts for this render only
    }
};

/**
 * Warns that a price configured for an OTA origin only applies once the prices are activated on
 * the OTA's side. Renders nothing unless `bookingOrigins` contains an OTA, or once the user chose
 * "don't show again" (kept per browser, like the language and currency choices).
 */
const OtaPriceActivationAlert = ({ bookingOrigins = [], sx }) => {
    const { t, i18n } = useTranslation();
    const translate = (key, options) => t(`ota_price_activation_alert.${key}`, options);
    const [hidden, setHidden] = useState(readHidden);

    if (hidden || !hasOtaOrigin(bookingOrigins)) return null;

    const origins = bookingOrigins
        .map(Number)
        .filter((id) => BookingOrigin.isOTABooking(id))
        .map((id) => [BookingOrigin.getTranslatedName(BookingOrigin.types, id, i18n.language)].flat().join(''))
        .join(', ');

    const handleDismiss = () => {
        writeHidden();
        setHidden(true);
    };

    return (
        // The dismiss button sits under the text rather than in MUI's `action` slot: on a phone the
        // action column took a fixed share of the width and squeezed the copy into a narrow strip.
        <Alert severity="warning" sx={{ width: '100%', ...sx }} title={translate('title', { origins })}>
            <Typography variant="body2">{translate('body', { origins })}</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
                {translate('steps')}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button color="inherit" size="small" onClick={handleDismiss} sx={{ textTransform: 'none' }}>
                    {translate('dismiss')}
                </Button>
            </Box>
        </Alert>
    );
};

export default OtaPriceActivationAlert;
